// Plain zustand `create`, NOT the `@/shared/lib/zustand` wrapper — on purpose.
// The wrapper registers the store with `resetAllStores()`, which logout calls,
// so switching this import would silently snap the app back to light on log out.
// The theme is a workstation preference, not session state; it outlives both.
import { create } from 'zustand';
import { ENV } from './env';
import { LocalStorageService } from './storages';

export type Theme = 'light' | 'dark';

/**
 * Dark mode is an internal affordance, not a product feature: it is available
 * everywhere except production, where the toggle isn't rendered. Gating the
 * *store* too (not just the button) means a stale `theme: "dark"` in
 * localStorage can't leak a half-styled dark UI in front of a customer.
 */
const isEnabled = !ENV.isProduction;

const applyTheme = (theme: Theme) => {
  // The `.dark` class on <html> is what re-points the tokens (app/styles/dark.css).
  document.documentElement.classList.toggle('dark', theme === 'dark');
};

const readStoredTheme = (): Theme => {
  if (!isEnabled) return 'light';

  try {
    return LocalStorageService.getTheme() === 'dark' ? 'dark' : 'light';
  } catch {
    // Malformed value from an older build — fall back rather than crash boot.
    return 'light';
  }
};

const initialTheme = readStoredTheme();

// Applied at import time so the first paint is already correct; the store is
// pulled in by AntdProvider, which is mounted for the whole app.
applyTheme(initialTheme);

type ThemeState = {
  theme: Theme;
  isThemeToggleAvailable: boolean;
  toggleTheme: () => void;
};

export const useThemeStore = create<ThemeState>()((set, get) => ({
  theme: initialTheme,
  isThemeToggleAvailable: isEnabled,
  toggleTheme: () => {
    if (!isEnabled) return;

    const nextTheme: Theme = get().theme === 'dark' ? 'light' : 'dark';

    LocalStorageService.setTheme(nextTheme);
    applyTheme(nextTheme);
    set({ theme: nextTheme });
  },
}));
